import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import {
  Container, Card, CardBody, Row, Col,
  Table, Badge, Button, Input, InputGroup, Spinner,
  Nav, NavItem, NavLink, UncontrolledTooltip,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { Search, Eye, RefreshCw, Printer, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle } from "lucide-react";
import axios from "axios";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import { getAuthHeader } from "../../../Components/Contexts/BackendConstants";
import {
  getAllFilledForms,
  getAnswers,
  getScopes,
  getTypes,
  getTargets,
  getFormTree,
  getChildForms,
  getFormDetail,
  getHazardTree,
  getPhotos,
  getPhotoUrl,
} from "../../../services/sahaInspectionService";
import { fuarService } from "../../../services/fuarService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag, currentLanguageId } from "../../../i18n";

// ─────────────────────────────────────────────────────────────────────────────
// Fuar seçici yok — sayfa her zaman aktif fuarı (MainContext.selectedFuarId)
// gösterir. Seviye filtresi dropdown değil, yan yana sekmeler. Durum filtresi
// kaldırıldı (Durum kolonu duruyor).
//
// Bu sayfa eski "Tutanak Listesi" ekranının (mock verili, /Management/Tutanaklar)
// yerini aldı — o sayfa 2026-09-12'de kaldırıldı.
// ─────────────────────────────────────────────────────────────────────────────
const FALLBACK_FUAR_ID = 298;

const LEVEL_TABS = [
  { key: "all", get label() { return i18n.t("common:tumu"); } },
  { key: "1",   get label() { return i18n.t("formlar:formInstances.denetim"); } },
  { key: "2",   get label() { return i18n.t("formlar:formInstances.uygunsuzluk"); } },
  { key: "3",   get label() { return i18n.t("formlar:formInstances.dof"); } },
];

const LEVEL_BADGES = {
  1: { get label() { return i18n.t("formlar:formInstances.denetim"); },     color: "primary" },
  2: { get label() { return i18n.t("formlar:formInstances.uygunsuzluk"); }, color: "warning" },
  3: { get label() { return i18n.t("formlar:formInstances.dof"); },         color: "success" },
};

const ANSWER_MAP = {
  1: { icon: "✅", get text() { return i18n.t("formlar:formInstances.uygun"); },       color: "success"   },
  2: { icon: "❌", get text() { return i18n.t("formlar:formInstances.uygunDegil"); }, color: "danger"    },
  3: { icon: "➖", text: "N/A",         color: "secondary" },
};
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
function formNo(form) {
  const id = String(form.id || form._id || "");
  return id ? `#${id.slice(-5).toUpperCase()}` : "—";
}
function LevelBadge({ id }) {
  const cfg = LEVEL_BADGES[id] || { label: id ?? "—", color: "secondary" };
  return <Badge color={cfg.color} pill className="px-2">{cfg.label}</Badge>;
}
function AnswerBadge({ value }) {
  const cfg = ANSWER_MAP[value] || { icon: "?", text: value, color: "light" };
  return <Badge color={cfg.color} className="rounded-pill">{cfg.icon} {cfg.text}</Badge>;
}
function statusBadge(form) {
  if (form.inspectionLevelId === 3 && form.isDone)
    return <Badge color="success"   pill className="px-2">{i18n.t("formlar:formInstances.kapaliDof")}</Badge>;
  if (form.isDone)
    return <Badge color="primary"   pill className="px-2">{i18n.t("formlar:formInstances.tamamlandi")}</Badge>;
  if (form.inspectionLevelId === 2)
    return <Badge color="danger"    pill className="px-2">{i18n.t("formlar:formInstances.acik")}</Badge>;
  return   <Badge color="secondary" pill className="px-2">{i18n.t("formlar:formInstances.devamEdiyor")}</Badge>;
}

// Risk sınıfı — DofPrintPage ile aynı eşikler ve renkler (baskıda da görünsün diye satır içi renk).
function riskHex(score) {
  if (!score) return "#aaa";
  if (score < 20)  return "#28a745";
  if (score < 70)  return "#1a6fb5";
  if (score < 200) return "#d4a017";
  if (score < 400) return "#e07b00";
  return "#c82333";
}
function riskClassLabel(score) {
  if (!score) return "—";
  if (score < 20)  return i18n.t("saha:dofPrint.onemsizRisk");
  if (score < 70)  return i18n.t("saha:dofPrint.olasiRisk");
  if (score < 200) return i18n.t("saha:dofPrint.onemliRisk");
  if (score < 400) return i18n.t("saha:dofPrint.esasliRisk");
  return i18n.t("saha:dofPrint.kabulEdilemezRisk");
}
function RiskScoreBadge({ score }) {
  if (score == null) return null;
  return (
    <span className="badge ms-1" style={{ background: riskHex(score), color: "#fff" }}>{score}</span>
  );
}

// Fotoğraflar auth başlığı ister → <img> için blob URL'e çevrilir (SahaUygunsuzlukList ile aynı yol).
async function resolvePhotos(list) {
  return Promise.all((Array.isArray(list) ? list : []).map(async (p) => {
    const raw = getPhotoUrl(p);
    if (!raw) return { ...p, _resolvedUrl: null };
    try {
      const res = await axios.get(raw, { headers: getAuthHeader(), responseType: "blob" });
      return { ...p, _resolvedUrl: URL.createObjectURL(res.data) };
    } catch {
      return { ...p, _resolvedUrl: raw };
    }
  }));
}

// /detail tüm içerik satırlarını (geçmiş dahil) verir; bir soru için en yeni satır sayılır.
// Satır yoksa (eski kayıtlar) latest-contents cevaplarından + form ağacından kurulur.
function buildAnswerRows(detail, answers, questionNames) {
  const byQuestion = new Map();
  (detail?.contents || []).forEach((c) => {
    const prev = byQuestion.get(c.questionId);
    if (!prev || new Date(c.createdAt || 0) >= new Date(prev.createdAt || 0)) byQuestion.set(c.questionId, c);
  });
  if (byQuestion.size) return Array.from(byQuestion.values());
  return Object.entries(answers || {}).map(([qId, a]) => ({
    questionId: Number(qId),
    questionName: questionNames[qId] || null,
    categoryId: a.categoryId,
    answerValue: a.value,
    hazardId: a.hazardId,
    riskId: a.riskId,
    nonconformityId: a.nonconformityId,
  }));
}

// ── Cevap listesi (modal içi) ─────────────────────────────────────────────────
// Başlıklar hiyerarşiyle birebir: hazard = Uygunsuzluk, risk = Tehlike, nonconformity = Risk.
function AnswerList({ rows, riskByNc }) {
  const { t } = useTranslation("formlar");
  if (!rows.length) return <p className="text-muted text-center my-3">{t("formInstances.cevapBulunamadi")}</p>;
  const groups = {};
  rows.forEach((r) => {
    const cat = r.categoryId ?? "—";
    if (!groups[cat]) groups[cat] = { name: r.categoryName, items: [] };
    groups[cat].items.push(r);
  });
  const dash = <span className="text-muted">—</span>;
  return (
    <div>
      {Object.entries(groups).map(([catId, group]) => (
        <div key={catId} className="mb-4">
          <h6 className="fw-semibold text-muted small mb-2">
            {group.name || <>{t("formInstances.kategori")}{catId}</>}
          </h6>
          <Table size="sm" bordered responsive className="small">
            <thead className="table-light">
              <tr>
                <th>{t("formInstances.soru")}</th><th>{t("formInstances.cevap")}</th>
                <th>{t("formInstances.uygunsuzluk")}</th><th>{t("formInstances.tehlike")}</th><th>{t("formInstances.risk")}</th>
              </tr>
            </thead>
            <tbody>
              {group.items.map((a) => (
                <tr key={a.questionId}>
                  <td>
                    {a.questionName
                      ? a.questionName
                      : <span className="text-muted">{t("formInstances.soru2")}{a.questionId}</span>}
                  </td>
                  <td><AnswerBadge value={a.answerValue} /></td>
                  <td>{a.hazardName || (a.hazardId ? <span className="text-muted">#{a.hazardId}</span> : dash)}</td>
                  <td>{a.riskName || (a.riskId ? <span className="text-muted">#{a.riskId}</span> : dash)}</td>
                  <td>
                    {a.nonconformityName || (a.nonconformityId ? <span className="text-muted">#{a.nonconformityId}</span> : dash)}
                    <RiskScoreBadge score={riskByNc[a.nonconformityId]?.riskDegeri} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ))}
    </div>
  );
}

// Seviye 2/3 (tek uygunsuzluk) formlarında: puanlama + öneriler + yasal dayanak.
function RiskBlock({ tree, nc }) {
  const { t } = useTranslation("formlar");
  if (!tree || !nc) return null;
  const score = nc.riskDegeri ?? null;
  const suggestions = tree.suggestions || [];
  const legal = tree.legalProcedures || [];
  return (
    <div className="mt-4">
      <h6 className="fw-bold mb-2">{t("formInstances.riskDegerlendirmesi")}</h6>
      <Table size="sm" bordered responsive className="small mb-2 text-center">
        <thead className="table-light">
          <tr>
            <th>{i18n.t("saha:dofPrint.olasilik")}</th><th>{i18n.t("saha:dofPrint.frekans")}</th>
            <th>{i18n.t("saha:dofPrint.siddet")}</th><th>{i18n.t("saha:dofPrint.riskDegeri")}</th>
            <th>{i18n.t("saha:dofPrint.mevcutRiskSinifi")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{nc.olasilik ?? "—"}</td><td>{nc.frekans ?? "—"}</td><td>{nc.siddet ?? "—"}</td>
            <td className="fw-bold" style={{ color: riskHex(score) }}>{score ?? "—"}</td>
            <td>
              {score != null
                ? <span className="badge" style={{ background: riskHex(score), color: "#fff" }}>{riskClassLabel(score)}</span>
                : "—"}
            </td>
          </tr>
        </tbody>
      </Table>
      {(suggestions.length > 0 || nc.oneri) && (
        <div className="small mb-2">
          <div className="text-muted">{i18n.t("saha:dofPrint.duzelticiOnleyiciOneriler")}</div>
          {suggestions.length > 0
            ? suggestions.map((s, i) => <div key={i}>{s.translated_name || s.inspection_suggestion_name || "—"}</div>)
            : <div>{nc.oneri}</div>}
        </div>
      )}
      {legal.length > 0 && (
        <div className="small">
          <div className="text-muted">{i18n.t("saha:dofPrint.yasalZorunlulukDayanak")}</div>
          {legal.map((lp, i) => <div key={i}>{lp.translated_name || lp.inspection_legal_procedure_name || "—"}</div>)}
        </div>
      )}
    </div>
  );
}

// Fotoğraf şeridi (modal + baskı)
function PhotoStrip({ title, photos, color }) {
  if (!photos.length) return null;
  return (
    <div className="mb-3">
      <div className={`small fw-semibold text-${color} mb-2`}>{title}</div>
      <div className="d-flex flex-wrap gap-2">
        {photos.map((p) => {
          const url = p._resolvedUrl || getPhotoUrl(p);
          return url ? (
            <a key={p.id} href={url} target="_blank" rel="noreferrer" title={p.originalFileName}>
              <img src={url} alt={p.originalFileName || ""}
                style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 8, border: "1px solid #ccc" }} />
            </a>
          ) : (
            <div key={p.id} className="border rounded-2 p-2 small text-truncate" style={{ maxWidth: 150 }}>{p.originalFileName}</div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function FormInstancesPage() {
  const { t: tx } = useTranslation("formlar");
  const { selectedFuarId } = useContext(MainContext);
  const fuarId = Number(selectedFuarId)
    || Number(localStorage.getItem("activeFairId"))
    || FALLBACK_FUAR_ID;

  // Referans haritaları
  const [fuarName,  setFuarName]  = useState("");
  const [scopeMap,  setScopeMap]  = useState({});  // scopeId → { name, targetEntity }
  const [typeMap,   setTypeMap]   = useState({});  // typeId  → ad
  const [targetMap, setTargetMap] = useState({});  // "stand:12" / "salon:3" / "fuar_alani:7" → { name, firma }

  // Liste
  const [forms,   setForms]   = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtreler
  const [activeTab, setActiveTab] = useState("all");
  const [search,    setSearch]    = useState("");

  // Sayfalama
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Detay
  const [detailModal,       setDetailModal]       = useState({ open: false, form: null });
  const [detailData,        setDetailData]        = useState(null);   // GET /form/{id}/detail (adlar, içerik satırları)
  const [answerRows,        setAnswerRows]        = useState([]);
  const [questionNames,     setQuestionNames]     = useState({});
  const [photos,            setPhotos]            = useState({ 1: [], 2: [], 3: [] });   // inspectionLevelId → fotoğraflar
  const [risk,              setRisk]              = useState({ byNc: {}, byHazard: {} });
  const [loadingDetail,     setLoadingDetail]     = useState(false);
  const [printBlocked,      setPrintBlocked]      = useState(false);
  // Satırdaki yazdır düğmesi: tıklamada açılan pencere burada bekler, detay yüklenince içine yazılır.
  // (Pencere tıklama anında açılmazsa tarayıcı açılır pencere engelleyicisi null döndürür.)
  const pendingPrintRef = useRef(null);
  // Bölüm 26-A — child forms (Level 1 → Uygunsuzluk/DÖF bağlantısı)
  const [childForms,        setChildForms]        = useState([]);
  const printRef = useRef(null);

  // ── Referans verilerini yükle ───────────────────────────────────────────
  useEffect(() => {
    document.title = i18n.t("formlar:formInstances.denetimKayitlariFds");

    getScopes().then(async (scopes) => {
      const sm = {};
      scopes.forEach((s) => {
        sm[s.inspection_scope_id] = {
          name:         s.translated_name || s.inspection_scope_name,
          targetEntity: s.target_entity || null,
        };
      });
      setScopeMap(sm);

      const tm = {};
      await Promise.all(scopes.map(async (s) => {
        const types = await getTypes(s.inspection_scope_id).catch(() => []);
        types.forEach((t) => {
          tm[t.inspection_type_id] = t.translated_name || t.inspection_type_name;
        });
      }));
      setTypeMap(tm);
    }).catch(() => {});

  }, []);

  // Hedef / Firma kolonlarının ad çözümlemesi: stand, salon ve fuar alanı hedefleri, sayfanın fuarı için
  useEffect(() => {
    let alive = true;
    Promise.all(["stand", "salon", "fuar_alani"].map((entity) =>
      getTargets(entity, fuarId).then((list) => [entity, list || []]).catch(() => [entity, []])
    )).then((groups) => {
      if (!alive) return;
      const map = {};
      groups.forEach(([entity, list]) => {
        list.forEach((t) => { map[`${entity}:${t.id}`] = { name: t.name, firma: t.firma }; });
      });
      setTargetMap(map);
    });
    return () => { alive = false; };
  }, [fuarId]);

  // Aktif fuarın adı (yalnızca başlık etiketi için)
  useEffect(() => {
    if (!fuarId) return;
    let alive = true;
    fuarService.getFuarDetail(fuarId)
      .then((d) => {
        if (!alive) return;
        setFuarName(d?.fuarTexts?.[0]?.fuarName || d?.fuar?.fuarName || d?.fuarName || "");
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [fuarId]);

  // ── Liste yükle (aktif fuar, tüm seviyeler — seviye filtresi istemci tarafı) ─
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllFilledForms({ fuarId, page: 1, limit: 500 });
      setForms(Array.isArray(data) ? data : []);
      setPage(1);
    } finally { setLoading(false); }
  }, [fuarId]);

  useEffect(() => { async function run() { await reload(); } run(); }, [reload]);

  // ── Detay modal aç ────────────────────────────────────────────────────
  // printWin: satırdaki "Yazdır" tıklamasında açılmış boş pencere (yalnız seviye 1 için) — veri gelince içine yazılır.
  const openDetail = useCallback(async (form, { printWin = null } = {}) => {
    const formId = form.id || form._id;
    setDetailModal({ open: true, form });
    setDetailData(null);
    setAnswerRows([]);
    setQuestionNames({});
    setPhotos({ 1: [], 2: [], 3: [] });
    setRisk({ byNc: {}, byHazard: {} });
    setChildForms([]);
    setLoadingDetail(true);
    if (pendingPrintRef.current && !pendingPrintRef.current.closed) pendingPrintRef.current.close();
    pendingPrintRef.current = printWin;
    try {
      const languageId = currentLanguageId();
      // Bölüm 26-A: Level 1 form → uygunsuzluk child formları da aynı turda (baskıya girsin diye)
      const [det, raw, tree, rawPhotos, kids] = await Promise.allSettled([
        getFormDetail(formId, languageId),
        getAnswers(formId),
        form.inspectionTypeId ? getFormTree(form.inspectionTypeId) : Promise.resolve(null),
        getPhotos(formId),
        form.inspectionLevelId === 1 ? getChildForms(formId) : Promise.resolve([]),
      ]);
      setChildForms(kids.status === "fulfilled" && Array.isArray(kids.value) ? kids.value : []);
      const detail  = det.status === "fulfilled" ? det.value : null;
      const answers = raw.status === "fulfilled" ? raw.value || {} : {};
      const names = {};
      if (tree.status === "fulfilled" && tree.value) {
        (tree.value?.categories || []).forEach((cat) => {
          (cat.questions || []).forEach((q) => { names[q.questionId] = q.questionName; });
        });
      }
      const rows = buildAnswerRows(detail, answers, names);

      // Puanlama: her Uygunsuzluk (hazard) için ağaç bir kez okunur, Risk (nonconformity) satırları buradan eşleşir
      const hazardIds = [...new Set(rows.map((r) => r.hazardId).filter(Boolean))];
      const trees = await Promise.all(hazardIds.map((h) => getHazardTree(h, { languageId }).catch(() => null)));
      const byNc = {}; const byHazard = {};
      trees.forEach((tr, i) => {
        if (!tr) return;
        byHazard[hazardIds[i]] = tr;
        (tr.risks || []).forEach((r) => (r.nonconformities || []).forEach((nc) => { byNc[nc.nonconformityId] = nc; }));
      });

      // Fotoğraflar seviyeye göre: 1 denetim, 2 uygunsuzluk (önce), 3 kapanış (sonra)
      const list = await resolvePhotos(rawPhotos.status === "fulfilled" ? rawPhotos.value : []);
      const byLevel = { 1: [], 2: [], 3: [] };
      list.forEach((p) => { (byLevel[p.inspectionLevelId] || byLevel[1]).push(p); });

      setDetailData(detail);
      setAnswerRows(rows);
      setQuestionNames(names);
      setRisk({ byNc, byHazard });
      setPhotos(byLevel);
    } finally { setLoadingDetail(false); }
  }, []);

  const closeDetail = () => {
    setDetailModal({ open: false, form: null });
    setPrintBlocked(false);
    if (pendingPrintRef.current && !pendingPrintRef.current.closed) pendingPrintRef.current.close();
    pendingPrintRef.current = null;
  };

  // ── Yazdırma ──────────────────────────────────────────────────────────
  // Seviye 2 (Uygunsuzluk) ve 3 (DÖF): stilli rapor sayfası (DofPrintPage), tıklamada yeni sekmede.
  // Seviye 1 (Denetim): modal içeriği, sayfanın stilleriyle birlikte yeni pencereye yazılır.
  const openDofReport = (form) => window.open(`/FdsSaha/dof-print/${form.id || form._id}`, "_blank");

  // Boş pencereyi tıklama anında aç (sonradan açılırsa açılır pencere engelleyicisi null döndürür).
  const openPrintWindow = () => {
    const win = window.open("", "_blank");
    if (!win) { setPrintBlocked(true); return null; }
    setPrintBlocked(false);
    win.document.write(`<p style="font-family:Arial,sans-serif;padding:16px">${i18n.t("formlar:formInstances.yukleniyor")}</p>`);
    return win;
  };

  const writePrintWindow = useCallback((win) => {
    const content = printRef.current?.innerHTML;
    if (!win || win.closed) return;
    if (!content) { win.close(); return; }
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map((n) => n.outerHTML).join("\n");
    const title = (document.title || "").replace(/</g, "&lt;");
    win.document.open();
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
      <base href="${window.location.origin}/">${styles}
      <style>
        body{background:#fff;padding:16px}
        *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .no-print{display:none!important}
      </style></head><body>${content}</body></html>`);
    win.document.close();
    let done = false;
    const go = () => { if (done || win.closed) return; done = true; win.focus(); win.print(); };
    win.addEventListener("load", () => setTimeout(go, 200));   // stiller + fotoğraflar yüklensin
    setTimeout(go, 3000);                                       // load hiç gelmezse
  }, []);

  // Seviye 1 satırındaki yazdır: detay yüklenince bekleyen pencereye yaz
  useEffect(() => {
    if (!pendingPrintRef.current || loadingDetail || !detailModal.open) return;
    const win = pendingPrintRef.current;
    pendingPrintRef.current = null;
    writePrintWindow(win);
  }, [loadingDetail, detailModal.open, writePrintWindow]);

  const onRowPrint = (form) => {
    if (form.inspectionLevelId === 2 || form.inspectionLevelId === 3) { openDofReport(form); return; }
    const win = openPrintWindow();
    openDetail(form, { printWin: win });
  };
  const onModalPrint = () => {
    const form = detailModal.form;
    if (form.inspectionLevelId === 2 || form.inspectionLevelId === 3) { openDofReport(form); return; }
    const win = openPrintWindow();
    writePrintWindow(win);
  };

  // ── Ad çözümleme yardımcıları ────────────────────────────────────────
  // Modal açıkken /detail'in çözdüğü adlar (salon, fuar alanı, stand, firma) listedeki tahminin önüne geçer.
  const resolved = (form) => (detailModal.form && (detailModal.form.id || detailModal.form._id) === (form.id || form._id) ? detailData : null);
  const scopeName = (form) =>
    resolved(form)?.inspectionScopeName || scopeMap[form.inspectionScopeId]?.name
      || `${tx("formInstances.kapsam")} #${form.inspectionScopeId ?? "?"}`;
  const typeName  = (form) =>
    resolved(form)?.inspectionTypeName || typeMap[form.inspectionTypeId]
      || tx("formInstances.tur", { v: form.inspectionTypeId ?? "?" });
  // Bir stand_id ile salon_id aynı sayı olabilir → hedef, kapsamın target_entity'siyle anahtarlanır
  const targetKey = (form) => {
    const entity = scopeMap[form.inspectionScopeId]?.targetEntity;
    return entity ? `${entity === "fuar_alan" ? "fuar_alani" : entity}:${form.scopeTargetId}` : null;
  };
  const targetName = (form) => {
    if (form.scopeTargetId == null) return "—";
    return resolved(form)?.scopeTargetName || targetMap[targetKey(form)]?.name || `#${form.scopeTargetId}`;
  };
  const firmaName = (form) =>
    resolved(form)?.firmaName || targetMap[targetKey(form)]?.firma
      || (form.firmaId ? `${tx("formInstances.firma")} #${form.firmaId}` : "—");
  const inspectorName = (form) => {
    const d = resolved(form);
    const full = d ? `${d.userName || ""} ${d.userSurname || ""}`.trim() : "";
    return full || form.userId || "—";
  };

  // ── İstemci filtresi ─────────────────────────────────────────────────
  const filtered = forms.filter((f) => {
    if (activeTab !== "all" && String(f.inspectionLevelId) !== activeTab) return false;
    if (search) {
      const s = search.toLowerCase();
      const haystack = [
        typeName(f), targetName(f), firmaName(f), scopeName(f),
        String(f.id || f._id || ""),
      ].join(" ").toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });

  // Sekme sayaçları (aktif fuarın tüm kayıtları üzerinden)
  const counts = forms.reduce((acc, f) => {
    acc[f.inspectionLevelId] = (acc[f.inspectionLevelId] || 0) + 1;
    return acc;
  }, {});

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageSlice  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  function handleTabChange(key) { setActiveTab(key); setPage(1); }

  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("formInstances.denetimKayitlari")} pageTitle={tx("formInstances.formYonetimi")} />

        {/* ── Seviye sekmeleri ── */}
        <Nav tabs className="mb-3">
          {LEVEL_TABS.map((tab) => (
            <NavItem key={tab.key}>
              <NavLink
                className={activeTab === tab.key ? "active fw-semibold" : ""}
                style={{ cursor: "pointer" }}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
                {tab.key === "all" ? (
                  <Badge color="dark" pill className="ms-2" style={{ fontSize: 10 }}>
                    {forms.length}
                  </Badge>
                ) : (
                  <Badge
                    color={LEVEL_BADGES[Number(tab.key)]?.color || "secondary"}
                    pill className="ms-2" style={{ fontSize: 10 }}
                  >
                    {counts[Number(tab.key)] || 0}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {/* ── Arama + aktif fuar ── */}
        <Row className="mb-3 g-2">
          <Col xs={12} sm={5} md={4}>
            <InputGroup size="sm">
              <span className="input-group-text bg-white">
                <Search size={13} className="text-muted" />
              </span>
              <Input
                placeholder={tx("formInstances.turStandFirmaFormNo")}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </InputGroup>
          </Col>
          <Col className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
            <small className="text-muted">
              {tx("formInstances.aktifFuar")}{" "}<strong>{fuarName || `#${fuarId}`}</strong>
            </small>
            <small className="text-muted">{filtered.length}{" "}{tx("formInstances.kayit")}</small>
            <Button color="primary" size="sm" onClick={reload} disabled={loading}>
              {loading ? <Spinner size="sm" /> : <><RefreshCw size={13} className="me-1" />{tx("common:yenile")}</>}
            </Button>
          </Col>
        </Row>

        {/* ── Tablo ── */}
        <Card>
          <CardBody className="p-0">
            <div className="table-responsive">
              <Table className="table-hover align-middle mb-0" bordered={false}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 100 }}>{tx("formInstances.formNo")}</th>
                    <th style={{ width: 120 }}>{tx("formInstances.seviye")}</th>
                    <th>{tx("formInstances.denetimTuru")}</th>
                    <th style={{ width: 110 }}>{tx("formInstances.kapsam")}</th>
                    <th style={{ width: 90 }}  className="text-center">{tx("formInstances.hedef")}</th>
                    <th style={{ width: 150 }}>{tx("formInstances.firma")}</th>
                    <th style={{ width: 140 }} className="text-center">{tx("formInstances.durum")}</th>
                    <th style={{ width: 150 }}>{tx("formInstances.tarih")}</th>
                    <th style={{ width: 90 }}  className="text-center">{tx("formInstances.islemler")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="text-center py-5">
                      <Spinner size="sm" className="me-2" />{tx("formInstances.yukleniyor")}
                    </td></tr>
                  ) : pageSlice.length === 0 ? (
                    <tr><td colSpan={9} className="text-center text-muted py-5">
                      {tx("common:kayitYok")}
                    </td></tr>
                  ) : pageSlice.map((form, idx) => {
                    const rowId = String(form.id || form._id || idx).replace(/[^a-zA-Z0-9]/g, "");
                    return (
                      <tr key={form.id || form._id || idx}>
                        {/* Form No */}
                        <td>
                          <code className="text-muted" style={{ fontSize: 11 }}>{formNo(form)}</code>
                          {form.parentFormId && (
                            <div>
                              <small className="text-muted" style={{ fontSize: 10 }}>
                                ↳ #{String(form.parentFormId).slice(-5).toUpperCase()}
                              </small>
                            </div>
                          )}
                        </td>

                        {/* Seviye */}
                        <td><LevelBadge id={form.inspectionLevelId} /></td>

                        {/* Tür */}
                        <td className="fw-semibold">{typeName(form)}</td>

                        {/* Kapsam */}
                        <td><small>{scopeName(form)}</small></td>

                        {/* Hedef */}
                        <td className="text-center">
                          <Badge color="light" className="text-dark border fw-semibold px-2">
                            {targetName(form)}
                          </Badge>
                        </td>

                        {/* Firma */}
                        <td><small>{firmaName(form)}</small></td>

                        {/* Durum */}
                        <td className="text-center">{statusBadge(form)}</td>

                        {/* Tarih */}
                        <td>
                          <small className="text-muted d-block">{fmtDate(form.createdAt)}</small>
                          {form.completedAt && (
                            <small className="text-success d-block" style={{ fontSize: 10 }}>
                              ✔ {fmtDate(form.completedAt)}
                            </small>
                          )}
                        </td>

                        {/* İşlemler */}
                        <td>
                          <div className="d-flex justify-content-center gap-1">
                            <button
                              type="button"
                              id={`view-${rowId}`}
                              className="btn btn-sm btn-soft-info p-1"
                              onClick={() => openDetail(form)}
                            >
                              <Eye size={13} />
                            </button>
                            <UncontrolledTooltip target={`view-${rowId}`}>{tx("common:goruntule")}</UncontrolledTooltip>

                            <button
                              type="button"
                              id={`print-${rowId}`}
                              className="btn btn-sm btn-soft-secondary p-1"
                              onClick={() => onRowPrint(form)}
                            >
                              <Printer size={13} />
                            </button>
                            <UncontrolledTooltip target={`print-${rowId}`}>{tx("formInstances.yazdir")}</UncontrolledTooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* ── Sayfalama ── */}
            {!loading && filtered.length > 0 && (
              <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">
                    {tx("formInstances.kayit2", { v: (safePage - 1) * pageSize + 1, v2: Math.min(safePage * pageSize, filtered.length), length: filtered.length })}
                  </small>
                  <Input type="select" bsSize="sm" style={{ width: 75 }} value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                    {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Input>
                  <small className="text-muted">{tx("formInstances.sayfa")}</small>
                </div>
                <div className="d-flex gap-1 flex-wrap">
                  <Button size="sm" color="light" disabled={safePage <= 1}
                    onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeft size={13} />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                    .reduce((acc, p, i, arr) => {
                      if (i > 0 && arr[i - 1] !== p - 1) acc.push("...");
                      acc.push(p); return acc;
                    }, [])
                    .map((p, i) => p === "..."
                      ? <span key={`e${i}`} className="px-1 align-self-center text-muted small">…</span>
                      : <Button key={p} size="sm" color={p === safePage ? "primary" : "light"}
                          onClick={() => setPage(p)}>{p}</Button>
                    )}
                  <Button size="sm" color="light" disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight size={13} />
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Detay Modal */}
        {detailModal.form && (
          <Modal isOpen={detailModal.open} toggle={closeDetail} size="xl" scrollable>
            <ModalHeader toggle={closeDetail}>
              {tx("formInstances.formDetayi", { formNo: formNo(detailModal.form) })}
            </ModalHeader>
            <ModalBody>
              <div ref={printRef}>
                <Row className="g-2 mb-4">
                  {[
                    [tx("formInstances.seviye"), <LevelBadge id={detailModal.form.inspectionLevelId} />],
                    [tx("formInstances.durum"), statusBadge(detailModal.form)],
                    [tx("formInstances.formTipi"), typeName(detailModal.form)],
                    [tx("formInstances.denetimYeri"), scopeName(detailModal.form)],
                    [tx("formInstances.hedef"), targetName(detailModal.form)],
                    [tx("formInstances.firma"), firmaName(detailModal.form)],
                    [tx("formInstances.denetci"), inspectorName(detailModal.form)],
                    [tx("formInstances.tarih"), fmtDate(detailModal.form.createdAt)],
                    ...(detailModal.form.completedAt
                      ? [[tx("formInstances.tamamlanma"), fmtDate(detailModal.form.completedAt)]]
                      : []),
                  ].map(([label, val]) => (
                    <Col key={label} md={3} xs={6}>
                      <div className="p-2 bg-light rounded">
                        <div className="text-muted" style={{ fontSize: "0.7rem" }}>{label}</div>
                        <div className="fw-semibold small">{val}</div>
                      </div>
                    </Col>
                  ))}
                </Row>

                {detailModal.form.parentFormId && (
                  <div className="p-2 border border-warning rounded text-warning mb-3 small">
                    {tx("formInstances.ustForm")}{" "}<strong>{detailModal.form.parentFormId}</strong>
                  </div>
                )}

                {(detailData?.notes || detailModal.form.notes) && (
                  <div className="p-2 bg-light rounded mb-3 small">
                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                      {detailModal.form.inspectionLevelId === 3 ? tx("formInstances.kapanisNotu") : tx("formInstances.notlar")}
                    </div>
                    <div className="fw-semibold" style={{ whiteSpace: "pre-wrap" }}>{detailData?.notes || detailModal.form.notes}</div>
                  </div>
                )}

                <h6 className="fw-bold mb-3">{tx("formInstances.verilenCevaplar")}</h6>
                {loadingDetail
                  ? <div className="text-center py-4"><Spinner size="sm" />{" "}{tx("formInstances.yukleniyor")}</div>
                  : <AnswerList rows={answerRows} riskByNc={risk.byNc} />}

                {/* Seviye 2/3: tek uygunsuzluğun puanlaması, önerileri, yasal dayanağı */}
                {!loadingDetail && detailModal.form.inspectionLevelId !== 1 && (() => {
                  const row = answerRows.find((r) => r.hazardId && risk.byHazard[r.hazardId]);
                  if (!row) return null;
                  const tree = risk.byHazard[row.hazardId];
                  // Risk (nonconformity) seçilmemişse yalnız ağaçta tek kayıt varsa o puanlanır (DofPrintPage ile aynı kural)
                  const all = (tree.risks || []).flatMap((r) => r.nonconformities || []);
                  const nc = row.nonconformityId ? risk.byNc[row.nonconformityId] : (all.length === 1 ? all[0] : null);
                  return <RiskBlock tree={tree} nc={nc} />;
                })()}

                {/* Fotoğraflar */}
                {!loadingDetail && (photos[1].length + photos[2].length + photos[3].length) > 0 && (
                  <div className="mt-4">
                    <h6 className="fw-bold mb-3">{tx("formInstances.fotograflar")}</h6>
                    <PhotoStrip color="primary" photos={photos[1]} title={tx("formInstances.fotografDenetim", { count: photos[1].length })} />
                    <PhotoStrip color="warning" photos={photos[2]} title={tx("formInstances.fotografOnce", { count: photos[2].length })} />
                    <PhotoStrip color="success" photos={photos[3]} title={tx("formInstances.fotografSonra", { count: photos[3].length })} />
                  </div>
                )}

                {/* Bölüm 26-A — Bu Denetimden Açılan Uygunsuzluklar (sadece Level 1) */}
                {detailModal.form?.inspectionLevelId === 1 && (
                  <div className="mt-4">
                    <h6 className="fw-bold mb-3">
                      <AlertTriangle size={15} className="me-1 text-warning" />
                      {tx("formInstances.buDenetimdenAcilanUygunsuzluklar")}
                    </h6>
                    {loadingDetail ? (
                      <div className="text-center py-3"><Spinner size="sm" />{" "}{tx("formInstances.yukleniyor")}</div>
                    ) : childForms.length === 0 ? (
                      <p className="text-muted small">
                        <CheckCircle size={13} className="me-1 text-success" />
                        {tx("formInstances.buDenetimeBagliUygunsuzlukFormu")}
                      </p>
                    ) : (
                      <Table size="sm" bordered responsive className="small">
                        <thead className="table-light">
                          <tr>
                            <th>{tx("formInstances.formId")}</th>
                            <th>{tx("formInstances.tetikleyenSoru")}</th>
                            <th>{tx("formInstances.seviye")}</th>
                            <th>{tx("formInstances.durum")}</th>
                            <th>{tx("formInstances.olusturulma")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {childForms.map((c) => (
                            <tr key={c.id || c._id}>
                              <td className="text-muted" style={{ fontSize: "0.7rem" }}>
                                {(c.id || c._id || "").slice(0, 10)}…
                              </td>
                              <td>
                                {c.sourceQuestionId
                                  ? (questionNames[c.sourceQuestionId]
                                      ? <strong>{questionNames[c.sourceQuestionId]}</strong>
                                      : <span className="text-muted">{tx("formInstances.soru2")}{c.sourceQuestionId}</span>)
                                  : <span className="text-muted">—</span>}
                              </td>
                              <td><LevelBadge id={c.inspectionLevelId} /></td>
                              <td>
                                {c.isDone
                                  ? <Badge color="success" className="rounded-pill">{tx("formInstances.kapali")}</Badge>
                                  : <Badge color="warning"  className="rounded-pill">{tx("formInstances.acik2")}</Badge>}
                              </td>
                              <td>{fmtDate(c.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              {printBlocked && (
                <small className="text-danger me-auto">{tx("formInstances.yazdirEngellendi")}</small>
              )}
              <Button color="secondary" outline size="sm" onClick={onModalPrint} disabled={loadingDetail}>
                <Printer size={13} className="me-1" />{tx("formInstances.yazdir")}
              </Button>
              <Button color="secondary" size="sm" onClick={closeDetail}>{tx("common:kapat")}</Button>
            </ModalFooter>
          </Modal>
        )}
      </Container>
    </div>
  );
}
